import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PartyPopper } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { formatDate } from '@/lib/dates'
import {
  getLastSeenRelease,
  latestRelease,
  setLastSeenRelease,
  type ReleaseItem,
} from '@/lib/releases'

/**
 * "What's new" pop-up.
 *
 * Auto-opens once per device per release version (tracked via
 * localStorage). Tap "Got it" → marks the version as seen and won't
 * pop again on that device until the next release lands. Anyone can
 * still re-open it from the user-menu surface if we add that link
 * later — the component exposes a controlled `open` mode for that.
 *
 * The release content lives in `src/lib/releases.ts` + the
 * `whats_new.releases.<version>` i18n namespace so adding a new release
 * is one PR with a code constant + a locale bump — no DB schema, no
 * backend round-trip.
 */
export function WhatsNewDialog({
  // Parents can force the dialog open (e.g. the "What's new" trigger
  // in the top nav after the user dismissed it). The dialog still
  // auto-opens on first visit for unseen releases regardless — this
  // prop ORs with the internal auto state, it doesn't replace it.
  forceOpen,
  onOpenChange,
}: {
  forceOpen?: boolean
  onOpenChange?: (next: boolean) => void
} = {}) {
  const { t } = useTranslation('whats_new')
  const release = latestRelease()
  // localStorage probe runs during the lazy initializer, not in an
  // effect — Vite SPA only (no SSR), so render-time read is safe.
  const [autoOpen, setAutoOpen] = useState(
    () => getLastSeenRelease() !== release.version,
  )

  const open = forceOpen || autoOpen
  const close = (next: boolean) => {
    if (!next) {
      setAutoOpen(false)
      // Persist the seen marker on dismiss only — opening for a peek
      // shouldn't suppress future renderings of the same version.
      setLastSeenRelease(release.version)
    }
    onOpenChange?.(next)
  }

  const releaseT = (suffix: string, fallback?: string) =>
    t(`releases.${release.version}.${suffix}`, { defaultValue: fallback ?? '' })

  return (
    <Dialog open={open} onOpenChange={close}>
      {/* The base DialogContent is a `grid` with `gap-4 p-6` and no
          height cap — fine for a 2-item dialog, but the 7+ items in a
          release blow past a phone's viewport with no way to scroll.
          Override to a vertically-flexed shell with capped height and a
          dedicated overflow-y-auto body section so the header + footer
          stay pinned and the items scroll between them. `100dvh`
          handles the iOS URL-bar shrink/expand transitions cleanly. */}
      <DialogContent className="flex max-h-[calc(100dvh-2rem)] flex-col gap-0 overflow-hidden p-0 sm:max-w-lg">
        <DialogHeader className="px-5 pt-5 pr-12 sm:px-6 sm:pt-6 sm:pr-14">
          <div className="mb-2 inline-flex h-10 w-10 items-center justify-center rounded-full bg-brand-light text-brand sm:h-12 sm:w-12">
            <PartyPopper className="size-5 sm:size-6" />
          </div>
          <DialogTitle className="text-lg sm:text-xl">
            {releaseT('title', t('default_title'))}
          </DialogTitle>
          <DialogDescription>
            {releaseT(
              'subtitle',
              t('default_subtitle', { date: formatDate(release.date) }),
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-4 pt-3 sm:px-6 sm:pb-5">
          <ul className="space-y-2 sm:space-y-3">
            {release.items.map((item) => (
              <ItemRow
                key={item.id}
                item={item}
                titleText={t(
                  `releases.${release.version}.items.${item.id}.title`,
                )}
                bodyText={t(
                  `releases.${release.version}.items.${item.id}.body`,
                )}
              />
            ))}
          </ul>
        </div>

        <DialogFooter className="border-t border-border bg-background px-5 py-3 sm:px-6 sm:py-4">
          <Button
            type="button"
            onClick={() => close(false)}
            className="w-full bg-brand text-white hover:bg-brand-dark hover:text-white sm:w-auto"
          >
            {t('dismiss')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function ItemRow({
  item,
  titleText,
  bodyText,
}: {
  item: ReleaseItem
  titleText: string
  bodyText: string
}) {
  const Icon = item.icon
  return (
    <li className="flex items-start gap-2.5 rounded-lg border border-border bg-background p-2.5 sm:gap-3 sm:p-3">
      <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-brand-light text-brand sm:h-9 sm:w-9">
        <Icon className="size-4" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold leading-snug text-foreground">
          {titleText}
        </div>
        <p className="mt-0.5 text-xs leading-snug text-muted-foreground">
          {bodyText}
        </p>
      </div>
    </li>
  )
}
